import { COMPLEMENT_PRIORITY_LABELS, type PortalViewOutput } from '@garage/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError } from '../../shared/api.js';
import { LinesList } from '../../garage/components/LinesList.js';
import { formatDateTime } from '../../garage/lib/format.js';
import { Button } from '../../garage/ui/button.js';
import { TextArea } from '../../garage/ui/form.js';
import { Money, hasAmount } from '../../garage/ui/money.js';
import { PORTAL_QUERY_KEY } from '../keys.js';
import { useTz } from '../timezone.js';
import { uuid } from '../uuid.js';
import { MessagePhotoGrid } from './MessagePhotoGrid.js';
import { FormActions } from '../../ui/FormActions.js';

type Complement = PortalViewOutput['complements'][number];
type Decision = 'accepted' | 'refused' | 'postponed' | 'change_requested';

/** Vague 6 : 4e réponse « Demander une modification » (commentaire obligatoire, jamais un accord). */
const CHOICES: { value: Decision; label: string; variant: 'primary' | 'secondary' | 'danger' }[] = [
  { value: 'accepted', label: 'Accepter', variant: 'primary' },
  { value: 'change_requested', label: 'Demander une modification', variant: 'secondary' },
  { value: 'postponed', label: 'Reporter', variant: 'secondary' },
  { value: 'refused', label: 'Refuser', variant: 'danger' },
];

const DONE: Record<Decision, string> = {
  accepted: 'Vous avez accepté ces travaux',
  refused: 'Vous avez refusé ces travaux',
  postponed: 'Vous avez choisi de reporter ces travaux',
  change_requested: 'Vous avez demandé une modification de ces travaux',
};
const DONE_BY_GARAGE: Record<Decision, string> = {
  accepted: 'Accord transmis au garage',
  refused: 'Refus transmis au garage',
  postponed: 'Report transmis au garage',
  change_requested: 'Demande de modification transmise au garage',
};

/**
 * Travaux complémentaires proposés au client, avec ses boutons de décision (`readOnly` : compte
 * rendu, aucun bouton). Chaque décision porte la version
 * exacte et une clé d'idempotence : stable pour un même choix (une nouvelle tentative après une
 * coupure réseau renvoie la même clé), et un double appui n'envoie qu'une requête.
 * Aucun montant n'est calculé ici : seuls ceux renvoyés par l'API sont affichés.
 */
export function ComplementDecision({
  complement: c,
  readOnly = false,
}: {
  complement: Complement;
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const tz = useTz();
  const [choice, setChoice] = useState<Decision | null>(null);
  const [comment, setComment] = useState('');
  const keysRef = useRef(new Map<string, string>());
  const inFlight = useRef(false);

  const decide = useMutation({
    mutationFn: (decision: Decision) => {
      const k = `${c.id}:${c.version}:${decision}`;
      let idempotencyKey = keysRef.current.get(k);
      if (!idempotencyKey) {
        idempotencyKey = uuid();
        keysRef.current.set(k, idempotencyKey);
      }
      return api('portal.decide', {
        params: { id: c.id },
        body: { version: c.version, decision, idempotencyKey, ...(decision === 'change_requested' ? { comment: comment.trim() } : {}) },
      });
    },
    onSuccess: (view) => {
      qc.setQueryData(PORTAL_QUERY_KEY, view);
      setChoice(null);
      setComment('');
    },
    onError: async (err) => {
      // Version remplacée ou décision déjà prise : on recharge la page à jour.
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
        setChoice(null);
        await qc.invalidateQueries({ queryKey: PORTAL_QUERY_KEY });
      }
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const decidable = c.decidable && !readOnly;
  const send = (d: Decision) => {
    if (inFlight.current) return;
    inFlight.current = true;
    decide.mutate(d);
  };

  // Photos justificatives et lignes (montants seulement s'ils sont renvoyés par l'API).
  const detail = (
    <>
      <MessagePhotoGrid className="mt-3 grid grid-cols-3 gap-2" photos={c.mediaIds.map((m) => ({ id: m, alt: 'Photo justificative' }))} />
      <div className="mt-3">
        <LinesList lines={c.lines.map((l) => ({ ...l, key: l.id }))} totalTtc={c.totals.totalTtc} showUnitPrice={false} />
      </div>
    </>
  );

  return (
    <article id={`travaux-${c.id}`} className="scroll-mt-4 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:p-5">
      <h3 className="text-lg font-semibold tracking-[-0.01em] text-ink">{c.title}</h3>
      <p className="text-sm text-ink-3">Proposé le {formatDateTime(c.sentAt, tz)}</p>
      {/* Council n°2 (08/10/2026) : priorité choisie par le garage, délai et mot du chef d'atelier (comme l'e-mail). */}
      {c.priority || c.extraDowntime ? (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
          {c.priority ? (
            <span className="rounded-full border border-line px-2.5 py-0.5 font-medium text-ink">Priorité : {COMPLEMENT_PRIORITY_LABELS[c.priority]}</span>
          ) : null}
          {c.extraDowntime ? <span>Immobilisation supplémentaire estimée : {c.extraDowntime}</span> : null}
        </p>
      ) : null}
      {c.workshopNote ? (
        <blockquote className="mt-2 text-sm text-ink-2 [unicode-bidi:isolate]">
          « {c.workshopNote} »{c.signedBy ? <span className="block text-ink-3">— {c.signedBy}</span> : null}
        </blockquote>
      ) : null}
      {/* C6 : le total (s'il est renvoyé) et la décision d'abord, visibles sans défiler ; le détail suit. */}
      {decidable && hasAmount(c.totals.totalTtc) ? (
        <p className="mt-2 flex items-baseline justify-between gap-3 text-base text-ink">
          <span>Total</span>
          <Money value={c.totals.totalTtc} suffix="TTC" className="text-lg font-semibold" />
        </p>
      ) : null}

      {c.decision ? (
        <p className="mt-4 rounded-xl bg-sunken p-3.5 font-medium text-ink" role="status">
          {c.decision.channel === 'portal' ? DONE[c.decision.decision] : DONE_BY_GARAGE[c.decision.decision]}{' '}
          le {formatDateTime(c.decision.decidedAt, tz)}.
          {c.decision.decision === 'change_requested' && c.decision.comment ? (
            <span className="mt-1 block font-normal [unicode-bidi:isolate]">« {c.decision.comment} »</span>
          ) : null}
          {c.decision.decision === 'change_requested' ? (
            <span className="mt-1 block font-normal">Le garage va vous proposer une nouvelle version. Aucun travail n’est engagé.</span>
          ) : null}
          {decidable ? (
            <span className="mt-1 block font-normal">
              Vous pouvez encore accepter ou refuser ces travaux.
            </span>
          ) : null}
        </p>
      ) : null}

      {decidable ? (
        <div className="mt-4">
          {choice ? (
            <div className="space-y-3 rounded-xl border border-brand-line bg-brand-soft p-3.5">
              <p className="text-base text-ink">
                {choice === 'accepted' ? (
                  <>
                    Vous acceptez ces travaux
                    {hasAmount(c.totals.totalTtc) ? (
                      <>
                        {' '}
                        pour <Money value={c.totals.totalTtc} suffix="TTC" className="font-medium" />
                      </>
                    ) : null}
                    .
                  </>
                ) : choice === 'refused' ? (
                  'Vous refusez ces travaux. Le garage ne les fera pas.'
                ) : choice === 'change_requested' ? (
                  'Dites au garage ce que vous souhaitez changer. Ce n’est pas un accord : il vous proposera une nouvelle version.'
                ) : (
                  'Vous souhaitez reporter ces travaux à plus tard.'
                )}
              </p>
              {choice === 'change_requested' ? (
                <TextArea
                  label="Votre demande de modification"
                  required
                  maxLength={2000}
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              ) : null}
              <FormActions inset>
                <Button variant="secondary" disabled={decide.isPending} onClick={() => setChoice(null)}>
                  Retour
                </Button>
                <Button busy={decide.isPending} disabled={choice === 'change_requested' && !comment.trim()} onClick={() => send(choice)}>
                  {choice === 'change_requested' ? 'Envoyer ma demande' : 'Confirmer'}
                </Button>
              </FormActions>
            </div>
          ) : (
            <div className="grid gap-2">
              {CHOICES.filter(
                (ch) => !(c.decision?.decision === 'postponed' && ch.value === 'postponed'),
              ).map((ch) => (
                <Button
                  key={ch.value}
                  variant={ch.variant}
                  className="min-h-(--ctl-lg) text-base font-semibold"
                  onClick={() => setChoice(ch.value)}
                >
                  {ch.label}
                </Button>
              ))}
            </div>
          )}
          {decide.error ? (
            <p
              role="alert"
              className="mt-3 rounded-xl border border-danger-line bg-danger-bg p-3.5 text-danger-fg"
            >
              {decide.error instanceof ApiError &&
              (decide.error.status === 409 || decide.error.status === 422)
                ? 'Ces travaux ont été modifiés ou ont déjà reçu une réponse. La page est à jour.'
                : 'Votre réponse n’a pas pu être envoyée. Vérifiez votre connexion et appuyez à nouveau sur Confirmer.'}
            </p>
          ) : null}
        </div>
      ) : null}
      {decidable ? (
        <div className="mt-5 border-t border-line pt-4">
          <h4 className="text-sm font-semibold text-ink-2">Détail des travaux</h4>
          {detail}
        </div>
      ) : (
        detail
      )}
      {!decidable && c.status === 'superseded' ? (
        <p
          className="mt-3 rounded-xl border border-warning-line bg-warning-bg p-3.5 text-sm text-warning-fg"
          role="status"
        >
          Le garage met à jour cette proposition : elle ne peut plus être acceptée. Vous recevrez la nouvelle
          version.
        </p>
      ) : null}
      {c.status === 'cancelled' ? (
        <p className="mt-3 rounded-xl bg-sunken p-3.5 text-sm text-ink-2" role="status">
          Proposition retirée par le garage : aucune réponse n’est attendue de votre part.
        </p>
      ) : null}
    </article>
  );
}
